import { formatList } from "@memorang/helpers/i18n";
import type { MixedItemTag } from "@memorang/types";
import type { SectionTag } from "@memorang/ui/components/FilterSections/types";
import type { TFunction } from "i18next";
import { TAG_TYPES, getQuestionTypesForSkill } from "./session-filters";

const MAX_NAMED_TITLES = 3;

export const resolvePremiumTags = ({
	freeTags,
	generalTags,
}: {
	freeTags: MixedItemTag[] | undefined;
	generalTags: MixedItemTag[] | undefined;
}): Record<string, MixedItemTag[]> => {
	if (!freeTags?.length || !generalTags?.length) {
		return {};
	}

	const freeIds = new Set(freeTags.map((tag) => tag.id));
	const freeTitlesByType = new Map<string, Set<string>>();
	for (const tag of freeTags) {
		const titles = freeTitlesByType.get(tag.type);
		if (titles) {
			titles.add(tag.title);
		} else {
			freeTitlesByType.set(tag.type, new Set([tag.title]));
		}
	}

	const premium: Record<string, MixedItemTag[]> = {};
	for (const tag of generalTags) {
		if (freeIds.has(tag.id)) {
			continue;
		}
		if (freeTitlesByType.get(tag.type)?.has(tag.title)) {
			continue;
		}
		const group = premium[tag.type];
		if (group) {
			group.push(tag);
		} else {
			premium[tag.type] = [tag];
		}
	}

	return premium;
};

export const buildLockedOptionsBannerLabel = ({
	lockedTitles,
	t,
	language,
}: {
	lockedTitles: string[];
	t: TFunction;
	language: string;
}): string | undefined => {
	if (lockedTitles.length === 0) {
		return undefined;
	}

	if (lockedTitles.length <= MAX_NAMED_TITLES) {
		return t("sessionConfig.quickStudy.lockedOptionsBanner", {
			names: formatList(language, lockedTitles),
		});
	}

	return t("sessionConfig.quickStudy.lockedOptionsBannerOverflow", {
		names: lockedTitles[0],
		count: lockedTitles.length - 1,
	});
};

const resolveSectionBannerTags = ({
	section,
	premiumTagsByType,
	selectedSkillTag,
}: {
	section: SectionTag;
	premiumTagsByType: Record<string, MixedItemTag[]> | undefined;
	selectedSkillTag: MixedItemTag | undefined;
}): MixedItemTag[] => {
	const sectionType = section.tags[0]?.type;
	if (!sectionType) {
		return [];
	}

	const premiumTags = premiumTagsByType?.[sectionType] ?? [];
	if (!premiumTags.length) {
		return section.tags.filter((tag) => tag.locked);
	}

	return sectionType === TAG_TYPES.questionType
		? getQuestionTypesForSkill(premiumTags, selectedSkillTag)
		: premiumTags;
};

/**
 * Resolves the unlock-banner label for a section: names the premium tags
 * for the section's type — scoped to the selected skill for Question Type
 * sections — falling back to the section's own locked tags when there is
 * no premium diff for that type.
 */
export const resolveSectionBannerLabel = ({
	section,
	premiumTagsByType,
	generalTags,
	selectedSkillId,
	t,
	language,
}: {
	section: SectionTag;
	premiumTagsByType: Record<string, MixedItemTag[]> | undefined;
	generalTags: MixedItemTag[] | undefined;
	selectedSkillId: number | undefined;
	t: TFunction;
	language: string;
}): string | undefined => {
	const selectedSkillTag = generalTags?.find(
		(tag) => tag.type === TAG_TYPES.skill && tag.id === selectedSkillId,
	);
	const bannerTags = resolveSectionBannerTags({
		section,
		premiumTagsByType,
		selectedSkillTag,
	});

	return buildLockedOptionsBannerLabel({
		lockedTitles: bannerTags.map((tag) => tag.title),
		t,
		language,
	});
};
